import React, { ReactNode, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { ListFilter } from "lucide-react";
import { useTranslation } from "react-i18next";
import PaginatorCst from "@components/PaginatorCst";
import FiltersAccordionCst from "@components/FiltersAccordionCst";
import SearchBar from "@components/SearchableMultiSelectListCst/SearchBar";
import { PagesID } from "@utils/utilsConfigurations";
import { PageFilterField } from "@hooks/usePageFilters";
import { DynamicFilterOption } from "@components/DynamicFilterCst/types";

interface EnrollmentListShellProps {
  search: string;
  onSearchSubmit: (value: string) => void;
  searchPlaceholder?: string;
  searchButtonLabel?: string;
  hideSearch?: boolean;
  filtersSlot?: ReactNode;
  filtersPageId?: PagesID;
  fieldFilters?: Record<string, any>;
  onApplyFilters?: (next: Record<string, any>) => void;
  filtersExcludeKeys?: string[];
  filterVisibility?: (
    field: PageFilterField,
    value: Record<string, any>,
  ) => boolean;
  filtersDynamicOptions?: Record<string, DynamicFilterOption[]>;
  filtersOptionsKeyOverrides?: Record<string, string>;
  headerSlot?: ReactNode;
  isLoading: boolean;
  isEmpty: boolean;
  emptyLabel: string;
  children: ReactNode;
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  onPageChange: (next: number) => void;
}

const EnrollmentListShell: React.FC<EnrollmentListShellProps> = ({
  search,
  onSearchSubmit,
  searchPlaceholder = "Cerca",
  searchButtonLabel = "Cerca",
  hideSearch = false,
  filtersSlot,
  filtersPageId,
  fieldFilters,
  onApplyFilters,
  filtersExcludeKeys,
  filterVisibility,
  filtersDynamicOptions,
  filtersOptionsKeyOverrides,
  headerSlot,
  isLoading,
  isEmpty,
  emptyLabel,
  children,
  page,
  pageSize,
  totalItems,
  totalPages,
  onPageChange,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("users-and-groups");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const hasActiveFilters = useMemo(
    () =>
      Object.values(fieldFilters || {}).some((val) => {
        if (val === undefined || val === null || val === "") return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      }),
    [fieldFilters],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {!hideSearch && (
        <SearchBar
          initialValue={search}
          onSubmit={onSearchSubmit}
          placeholder={searchPlaceholder}
          buttonLabel={searchButtonLabel}
          filtersSlot={filtersSlot}
        />
      )}

      {filtersPageId && (
        <Box sx={{ display: "flex" }}>
          <Button
            variant="outlined"
            startIcon={<ListFilter size={16} />}
            onClick={() => setFiltersOpen((prev) => !prev)}
            sx={{
              height: "40px",
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              flexShrink: 0,
              transition: "none",
              borderColor:
                hasActiveFilters || filtersOpen
                  ? theme.palette.primary.main
                  : theme.palette.divider,
              color:
                hasActiveFilters || filtersOpen
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
              backgroundColor:
                hasActiveFilters || filtersOpen
                  ? theme.palette.action.hover
                  : "transparent",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            {t("filters.button")}
          </Button>
        </Box>
      )}

      {filtersPageId && onApplyFilters && (
        <FiltersAccordionCst
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          pageId={filtersPageId}
          value={fieldFilters || {}}
          onApply={onApplyFilters}
          excludeKeys={filtersExcludeKeys}
          filterVisibility={filterVisibility}
          dynamicOptions={filtersDynamicOptions}
          optionsKeyOverrides={filtersOptionsKeyOverrides}
        />
      )}

      {headerSlot}

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isEmpty ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 6,
            color: theme.palette.text.tertiary,
            fontSize: "13px",
          }}
        >
          <Typography>{emptyLabel}</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            maxHeight: 480,
            overflowY: "auto",
          }}
        >
          {children}
        </Box>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <PaginatorCst
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={onPageChange}
          />
        </Box>
      )}
    </Box>
  );
};

export default EnrollmentListShell;
